import { describeTarget, interactiveTarget } from './describe-target';
import type { UiActivity, UiEvent } from './types';
import { widgetOf } from './widget-markers';

/**
 * The DOM events recorded and the entry event each becomes; `focusin` is `focus` that
 * bubbles. All but `change` and `submit` are composed, so they come out of an open shadow
 * root; those two do not, a gap accepted for what lives under `WidgetRoot` (PSDK-6558).
 */
const DOM_EVENTS: Readonly<Record<string, UiEvent>> = {
  click: 'click',
  input: 'input',
  change: 'change',
  keydown: 'keydown',
  focusin: 'focus',
  submit: 'submit'
};

/** Keys with a meaning of their own; any other printable key is a `char`, the rest `other`. */
const NAMED_KEYS: ReadonlySet<string> = new Set([
  'Enter',
  'Escape',
  'Tab',
  'Backspace',
  'Delete',
  'ArrowUp',
  'ArrowDown',
  'ArrowLeft',
  'ArrowRight',
  'Home',
  'End',
  'PageUp',
  'PageDown'
]);

const MODIFIER_KEYS: ReadonlySet<string> = new Set(['Shift', 'Control', 'Alt', 'Meta', 'CapsLock']);

/**
 * In a field, what a key does shows up as the `input` event it causes, and a keystroke
 * fires `keydown` and `input` in turn: recording both would keep a run of typing from
 * folding into one entry. So in a field only the keys that act are recorded as keys.
 */
const KEYS_RECORDED_IN_FIELDS: ReadonlySet<string> = new Set(['Enter', 'Escape', 'Tab']);

const EDITABLE_TAGS: ReadonlySet<string> = new Set(['input', 'textarea', 'select']);

/**
 * A key's category, never the key: a character typed may be part of a name or a password.
 * A modifier on its own is not an action and yields nothing.
 */
export function keyCategory(key: string): string | undefined {
  if (MODIFIER_KEYS.has(key)) {
    return undefined;
  }
  if (NAMED_KEYS.has(key)) {
    return key;
  }
  return key.length === 1 ? 'char' : 'other';
}

function isEditable(element: Element): boolean {
  return EDITABLE_TAGS.has(element.localName) || element.closest('[contenteditable]') !== null;
}

/** The key to record for a `keydown` on `element`, if any. */
function recordedKey(event: KeyboardEvent, element: Element): string | undefined {
  const key = keyCategory(event.key);
  if (key === undefined || (isEditable(element) && !KEYS_RECORDED_IN_FIELDS.has(key))) {
    return undefined;
  }
  return key;
}

/** `undefined` for an event with nothing to say: a modifier key, a target that is no element. */
export function toUiActivity(event: Event, time: number): UiActivity | undefined {
  const uiEvent = DOM_EVENTS[event.type];
  if (uiEvent === undefined) {
    return undefined;
  }
  const path = event.composedPath();
  const element = interactiveTarget(path);
  if (element === undefined) {
    return undefined;
  }
  const entry: UiActivity = {
    kind: 'ui',
    time,
    event: uiEvent,
    target: describeTarget(element),
    count: 1,
    ...widgetOf(path)
  };
  if (uiEvent === 'keydown') {
    const key = recordedKey(event as KeyboardEvent, element);
    if (key === undefined) {
      return undefined;
    }
    entry.key = key;
  }
  return entry;
}

export interface DomRecorderOptions {
  /** Where the listeners go: the window, so that every widget's events pass through. */
  target?: EventTarget;
  now?: () => number;
}

/**
 * One capture-phase listener per event type on the window: it runs before any widget's
 * own handler and sees the event whether or not a handler stops it. Returns the function
 * that removes the listeners.
 */
export function observeUserActivity(
  record: (entry: UiActivity) => void,
  { target = window, now = Date.now }: DomRecorderOptions = {}
): () => void {
  const onEvent = (event: Event) => {
    try {
      const entry = toUiActivity(event, now());
      if (entry !== undefined) {
        record(entry);
      }
    } catch {
      // Remembering an event must never interfere with handling it.
    }
  };
  const types = Object.keys(DOM_EVENTS);
  types.forEach((type) => target.addEventListener(type, onEvent, { capture: true, passive: true }));
  return () => {
    types.forEach((type) => target.removeEventListener(type, onEvent, { capture: true }));
  };
}
